/**
 * Preview for publish_logs published/unpublished item arrays.
 * Shows collection, linked entity id, name, and destination level when present.
 */
import React from "react";
import {
  PropertyPreviewProps,
  useNavigationController,
  useSideEntityController,
} from "@firecms/core";
import { DESTINATION_LEVELS } from "../lib/destinationLevels";

type LogItem = {
  collection?: string;
  id?: string;
  name?: string;
  level?: string;
};

function levelLabel(level: string | undefined): string | null {
  if (!level) return null;
  return DESTINATION_LEVELS[level as keyof typeof DESTINATION_LEVELS] ?? level;
}

export function PublishLogItemsPreview({
  value,
}: PropertyPreviewProps<LogItem[]>) {
  const navigation = useNavigationController();
  const sideEntityController = useSideEntityController();

  if (!Array.isArray(value) || value.length === 0) {
    return <span className="text-text-secondary text-xs">None</span>;
  }

  return (
    <div className="flex flex-col gap-2 text-xs w-full">
      {value.map((item, index) => {
        const collectionId = typeof item?.collection === "string" ? item.collection : "";
        const entityId = typeof item?.id === "string" ? item.id : "";
        const name = typeof item?.name === "string" ? item.name : "";
        const level = levelLabel(typeof item?.level === "string" ? item.level : undefined);
        const collection = collectionId
          ? navigation.getCollection(collectionId)
          : undefined;
        const canOpen = Boolean(collectionId && entityId && collection);

        return (
          <div
            key={`${collectionId}-${entityId}-${index}`}
            className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-surface-200 dark:border-surface-700 last:border-b-0 py-1.5"
          >
            <span className="font-medium text-text-secondary min-w-[7rem]">
              {collectionId || "—"}
            </span>
            {canOpen ? (
              <button
                type="button"
                className="font-mono text-primary underline underline-offset-2 hover:opacity-80 bg-transparent border-0 p-0 cursor-pointer text-left"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  sideEntityController.open({
                    path: collectionId,
                    entityId,
                    collection,
                    updateUrl: true,
                  });
                }}
              >
                {entityId}
              </button>
            ) : (
              <span className="font-mono">{entityId || "—"}</span>
            )}
            <span className="text-text-primary">{name || "—"}</span>
            {level ? (
              <span className="text-text-secondary">Level: {level}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
